import React, { useState, useEffect } from "react";
import { Formik, Field, Form } from "formik";
import * as Yup from "yup";
import {
  TournamentStatus,
  ParticipantType,
  ListingType,
  PrizePoolType,
  GamingPlatforms,
} from "../../utils/enums.utils";
import { regions } from "../../utils/common.utils";
import styles from "./tournamentFilter.module.css";

interface TournamentStatusButtonsProps {
  currentStatus: string;
  onStatusChange: (status: string) => void;
}

export const TournamentStatusButtons: React.FC<TournamentStatusButtonsProps> = ({
  currentStatus,
  onStatusChange,
}) => {
  const statusButtons = [
    {
      value: `${TournamentStatus.ONGOING},${TournamentStatus.UPCOMING},${TournamentStatus.COMPLETED}`,
      label: "All",
    },
    { value: TournamentStatus.UPCOMING, label: "Upcoming" },
    { value: TournamentStatus.ONGOING, label: "Live" },
    { value: `${TournamentStatus.COMPLETED}`, label: "COMPLETED" },
  ];

  return (
    <div className={styles.statusButtonsContainer}>
      <div className={styles.statusButtonsWrapper}>
        {statusButtons.map(({ value, label }) => (
          <div key={value} className={styles.statusButtonWrapper}>
            <button
              onClick={() => onStatusChange(value)}
              className={`btn btn-sm rounded-pill px-3 ${styles.statusButton} ${
                currentStatus === value ? styles.active : ""
              }`}
            >
              {label}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

interface TournamentFiltersProps {
  allGames: Array<{ _id: string; name: string }>;
  onFilterChange: (filters: any) => void;
  onClearFilters: () => void;
  initialValues?: {
    gameId?: string;
    platform?: string;
    isBettingEnabled?: string;
    listingType?: string;
    participantType?: string;
    region?: string;
    entryFee?: string;
    prizePoolType?: string;
    tournamentStatus?: string;
  };
}

const FILTER_PARAMS = [
  "gameId",
  "platform",
  "isBettingEnabled",
  "listingType",
  "participantType",
  "region",
  "entryFee",
  "prizePoolType",
  "tournamentStatus",
];

export const TournamentFilters: React.FC<TournamentFiltersProps> = ({
  allGames,
  onFilterChange,
  onClearFilters,
  initialValues = {},
}) => {
  const [showFilters, setShowFilters] = useState(false);

  const initialFormValues = {
    gameId: initialValues.gameId || "",
    platform: initialValues.platform || "",
    isBettingEnabled: initialValues.isBettingEnabled || "",
    listingType: initialValues.listingType || "",
    participantType: initialValues.participantType || "",
    region: initialValues.region || "",
    entryFee: initialValues.entryFee || "",
    prizePoolType: initialValues.prizePoolType || "",
    tournamentStatus:
      initialValues.tournamentStatus || `${TournamentStatus.ONGOING},${TournamentStatus.UPCOMING}`,
  };

  const validationSchema = Yup.object({
    gameId: Yup.string(),
    platform: Yup.string(),
    isBettingEnabled: Yup.string(),
    listingType: Yup.string(),
    participantType: Yup.string(),
    region: Yup.string(),
    entryFee: Yup.string(),
    prizePoolType: Yup.string(),
  });

  const handleSubmit = (
    values: any,
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ) => {
    setTimeout(() => {
      onFilterChange(values);
      setSubmitting(false);
    }, 400);
  };

  const handleClearFilters = () => {
    onClearFilters();
  };

  const renderFilterFields = () => (
    <>
      {renderGameSelect()}
      {renderPlatformSelect()}
      {renderBettingSelect()}
      {renderRegionSelect()}
      {renderListingTypeSelect()}
      {renderParticipantTypeSelect()}
      {renderEntryFeeSelect()}
      {renderPrizePoolSelect()}
    </>
  );

  const renderGameSelect = () => (
    <div className="col-lg-3 col-md-4 col-6 my-1 px-1">
      <Field
        name="gameId"
        as="select"
        className="fs-14 rounded-3 ps-3 text-white w-100 bg-black height-45"
      >
        <option value="">All Game</option>
        {allGames?.map((game, i) => (
          <option value={game._id} key={i}>
            {game.name}
          </option>
        ))}
      </Field>
    </div>
  );

  const renderPlatformSelect = () => (
    <div className="col-lg-3 col-md-4 col-6 my-1 px-1">
      <Field
        name="platform"
        as="select"
        className="fs-14 rounded-3 ps-3 text-white w-100 bg-black height-45"
      >
        <option value="">Platform</option>
        <option value={GamingPlatforms.PC}>PC</option>
        <option value={GamingPlatforms.MOBILE}>Mobile</option>
        <option value={GamingPlatforms.NINTENDO}>Switch</option>
        <option value={GamingPlatforms.PS}>PS</option>
        <option value={GamingPlatforms.XBOX}>X-box</option>
      </Field>
    </div>
  );

  const renderBettingSelect = () => (
    <div className="col-lg-3 col-md-4 col-6 my-1 px-1">
      <Field
        name="isBettingEnabled"
        as="select"
        className="fs-14 rounded-3 ps-3 text-white w-100 bg-black height-45"
      >
        <option value="">Staking</option>
        <option value={JSON.stringify(false)}>Disabled</option>
        <option value={JSON.stringify(true)}>Enabled</option>
      </Field>
    </div>
  );

  const renderRegionSelect = () => (
    <div className="col-lg-3 col-md-4 col-6 my-1 px-1">
      <Field
        name="region"
        as="select"
        className="fs-14 rounded-3 ps-3 text-white w-100 bg-black height-45"
      >
        <option value="">Region</option>
        {regions?.map((region, i) => (
          <option key={i} value={region}>
            {region}
          </option>
        ))}
      </Field>
    </div>
  );

  const renderListingTypeSelect = () => (
    <div className="col-lg-3 col-md-4 col-12 pe-1 my-1 px-1">
      <Field
        name="listingType"
        as="select"
        className="fs-14 rounded-3 ps-3 text-white w-100 bg-black height-45"
      >
        <option value="">Listing Type</option>
        <option value={ListingType.REGULAR}>Regular</option>
        <option value={ListingType.PREMIUM}>Premium</option>
      </Field>
    </div>
  );

  const renderParticipantTypeSelect = () => (
    <div className="col-lg-3 col-md-4 col-12 ps-1 my-1 px-1">
      <Field
        name="participantType"
        as="select"
        className="fs-14 rounded-3 ps-3 text-white w-100 bg-black height-45"
      >
        <option value="">Participant Type</option>
        <option value={ParticipantType.SINGLE}>Single</option>
        <option value={ParticipantType.TEAM}>Team</option>
      </Field>
    </div>
  );

  const renderEntryFeeSelect = () => (
    <div className="col-lg-3 col-md-4 col-6 my-1 px-1">
      <Field
        name="entryFee"
        as="select"
        className="fs-14 rounded-3 ps-3 text-white w-100 bg-black height-45"
      >
        <option value="">Entry</option>
        <option value={JSON.stringify(false)}>Free</option>
        <option value={JSON.stringify(true)}>Paid</option>
      </Field>
    </div>
  );

  const renderPrizePoolSelect = () => (
    <div className="col-lg-3 col-md-4 col-6 my-1 px-1">
      <Field
        name="prizePoolType"
        as="select"
        className="fs-14 rounded-3 ps-3 text-white w-100 bg-black height-45"
      >
        <option value="">Prize Pool</option>
        <option value="NONE_PRIZE_POOL_FILTER">None</option>
        <option value={PrizePoolType.FIXED}>Fixed</option>
        <option value={PrizePoolType.CROWDFUNDED}>Crowd Funded</option>
      </Field>
    </div>
  );

  const renderFilterButtons = () => (
    <div className="d-flex justify-content-center w-100 my-2">
      <button
        className="mx-auto bg-black text-warning border-0 rounded-pill px-3 py-1"
        type="submit"
      >
        Apply
      </button>
      <button
        className="mx-auto bg-black text-warning border-0 rounded-pill px-3 py-1"
        type="button"
        onClick={handleClearFilters}
      >
        Clear
      </button>
    </div>
  );

  return (
    <Formik
      enableReinitialize
      initialValues={initialFormValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ handleSubmit }) => (
        <Form>
          <div className="row p-2">{renderFilterFields()}</div>
          {renderFilterButtons()}
        </Form>
      )}
    </Formik>
  );
};

export default TournamentFilters;
